import React, { useEffect, useMemo } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import BusinessOverviewLayer from './BusinessOverviewLayer';
import VenueManagementLayer from './VenueManagementLayer';
import AllVenuesLayer from './AllVenuesLayer';
import CategoriesLayer from './CategoriesLayer';
import VenueVerificationLayer from './VenueVerificationLayer';
import SubscriptionControlLayer from './SubscriptionControlLayer';
import MySubscriptionLayer from './MySubscriptionLayer';
import UserManagementLayer from './UserManagementLayer';
import ContentModerationLayer from './ContentModerationLayer';
import PositionManagementLayer from './PositionManagementLayer';
import EmployeeManagementLayer from './EmployeeManagementLayer';
import VendorAnalyticsTable from './child/VendorAnalyticsTable';
import { useAuth } from '../context/AuthContext';
import { useApplicationContext } from '../context/ApplicationContext';
import { applicationTabs } from '../helper/applicationTabs';

const APP_TYPE = process.env.REACT_APP_APP_TYPE || 'dev';

const ApplicationsLayer = () => {
  const { tabId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { activeTabId, setActiveTabId } = useApplicationContext();

  const allowedTabs = useMemo(() => {
    const checkPermission = (permission) => {
      if (!user) return false;
      if (permission === 'authenticated') return true;
      if (permission === 'ROLE_ADMIN') return user.role === 'admin';
      if (user.role === 'admin') return true;
      if (!user.permissions) return false;
      return user.permissions.includes(permission);
    };

    return applicationTabs
      .filter(tab => checkPermission(tab.permission))
      .filter(tab => {
        if (APP_TYPE === 'admin') {
          return tab.id !== 'my-plan' && tab.id !== 'my-venues' && tab.id !== 'overview';
        }
        return true;
      });
  }, [user]);

  // Map IDs to Content
  const contentMap = {
    'overview': <BusinessOverviewLayer />,
    'my-venues': <VenueManagementLayer />,
    'my-plan': <MySubscriptionLayer />,
    'all-venues': <AllVenuesLayer />,
    'categories': <CategoriesLayer />,
    'venue-verification': <VenueVerificationLayer />,
    'venue-analytics': <VendorAnalyticsTable />,
    'vendor-analytics': <VendorAnalyticsTable />,
    'subscriptions': <SubscriptionControlLayer />,
    'user-management': <UserManagementLayer />,
    'moderation': <ContentModerationLayer />,
    'positions': <PositionManagementLayer />,
    'employees': <EmployeeManagementLayer />
  };

  useEffect(() => {
    // 1. Check if URL has a tabId param (e.g. /applications/all-venues)
    if (tabId && allowedTabs.some(t => t.id === tabId)) {
      if (activeTabId !== tabId) {
        setActiveTabId(tabId);
      }
      localStorage.setItem('hubscroll_admin_tab', tabId);
      return;
    }

    // 2. Check query param ?tab=
    const searchParams = new URLSearchParams(location.search);
    const queryTab = searchParams.get('tab');
    if (queryTab && allowedTabs.some(t => t.id === queryTab)) {
      if (activeTabId !== queryTab) {
        setActiveTabId(queryTab);
      }
      localStorage.setItem('hubscroll_admin_tab', queryTab);
      return;
    }

    // 3. Check localStorage saved tab if on /applications root
    const savedTab = localStorage.getItem('hubscroll_admin_tab');
    if (savedTab && allowedTabs.some(t => t.id === savedTab)) {
      if (activeTabId !== savedTab) {
        setActiveTabId(savedTab);
      }
      if (location.pathname === '/applications') {
        navigate(`/applications/${savedTab}`, { replace: true });
      }
      return;
    }

    // 4. Default fallback to first allowed tab (which is 'overview' for business users)
    if (allowedTabs.length > 0) {
      const isAllowed = allowedTabs.find(tab => tab.id === activeTabId);
      const targetTab = isAllowed ? activeTabId : allowedTabs[0].id;
      if (activeTabId !== targetTab) {
        setActiveTabId(targetTab);
      }
      if (location.pathname === '/applications') {
        navigate(`/applications/${targetTab}`, { replace: true });
      }
    }
  }, [tabId, location.search, location.pathname, allowedTabs, activeTabId, setActiveTabId, navigate]);

  const activeContent = contentMap[activeTabId] || contentMap['overview'] || contentMap[allowedTabs[0]?.id];

  return (
    <div className="card h-100 p-0 radius-12 shadow-none border-0 bg-transparent" key={activeTabId}>
      <div className="card-body p-0">
        {activeContent}
      </div>
    </div>
  );
};

export default ApplicationsLayer;
